// src/components/user/UserDashboard.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';

// ✅ MUI from central lib file
import {
  Box, Container, Typography, Paper, Stack, Button,
} from '../../lib/mui';

import { auth } from '../../firebase/firebase';
import { repo } from '../../dbservice/repo'; // 👈 Import repo to fetch user data

// ─────────────────────────────────────────────
// Palette
// ─────────────────────────────────────────────

const COLORS = {
  primary:     '#3B82F6',
  danger:      '#EF4444',
  textPrimary: '#111827',
  textSecondary: '#6b7280',
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

const UserDashboard = () => {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [error, setError] = useState(null);

  const [userInfo, setUserInfo] = useState({
    name: '', email: '', company: '',
  });

  // ── Auth ─────────────────────────────────────
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          setCurrentUser(user);
          await loadUserProfile(user);
        } else {
          setCurrentUser(null);
        }
      } catch (err) {
        console.error('[Dashboard] Auth error:', err);
        setError('Failed to authenticate user');
      } finally {
        setLoadingUser(false);
      }
    });
    return () => unsubAuth();
  }, []);

  // ── Fetch user profile from Database ─────────
  const loadUserProfile = async (user) => {
    try {
      // 1. Fetch from your database
      const profile = await repo.fetchById('users', user.uid);
      
      // 2. Resolve the best available name
      const resolvedName = 
        profile?.name || 
        profile?.displayName || 
        user.displayName || 
        user.email?.split('@')[0] || 
        'User';

      setUserInfo({
        name: resolvedName,
        email: profile?.email || user.email || '',
        company: profile?.company || '',
      });
    } catch (err) {
      console.error('[Dashboard] loadUserProfile error:', err);
      // Even if DB fails, fallback to Auth data so the page doesn't break
      setUserInfo({
        name: user.displayName || user.email?.split('@')[0] || 'User',
        email: user.email || '',
        company: '',
      });
    }
  };

  // ── Proceed handler ──────────────────────────
  const handleProceed = () => {
    navigate('/user/assignedassets');
  };

  // ── Guards ───────────────────────────────────
  if (error) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '60vh', gap: 2,
        bgcolor: '#f5f7fa',
      }}>
        <AlertTriangle size={48} color={COLORS.danger} />
        <Typography variant="h6" color={COLORS.textPrimary}>Error Loading Dashboard</Typography>
        <Typography color={COLORS.textSecondary}>{error}</Typography>
        <Button variant="contained" onClick={() => window.location.reload()}
          sx={{ bgcolor: COLORS.primary }}>
          Retry
        </Button>
      </Box>
    );
  }

  if (loadingUser) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh', bgcolor: '#f5f7fa',
      }}>
        <Typography color={COLORS.textSecondary}>Loading…</Typography>
      </Box>
    );
  }

  if (!currentUser) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '60vh', gap: 2,
        bgcolor: '#f5f7fa',
      }}>
        <Typography variant="h6" color={COLORS.textPrimary}>Authentication Required</Typography>
        <Typography color={COLORS.textSecondary}>Please sign in to access your dashboard</Typography>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // LANDING VIEW
  // ═══════════════════════════════════════════════════════════
  const features = [
    { title: "QR Automation",     desc: "Automated QR code generation and scanning." },
    { title: "AI Maintenance",    desc: "AI-driven predictive maintenance and renewals." },
    { title: "RBAC",              desc: "Role-based access for Super Admin, Admin, and Users." },
    { title: "Location Tracking", desc: "Location and department-based tracking." },
    { title: "Audit Logs",        desc: "Integrated audit logs and activity monitoring." },
  ];

  return (
    <Box sx={{ bgcolor: "#f6f8fb", minHeight: "100vh" }}>
      {/* ══════════ HERO ══════════ */}
      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 4, md: 6 } }}>
        <Stack alignItems="center" spacing={3}>
          {/* Logo card */}
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: 2,
              px: { xs: 3, sm: 5 },
              py: { xs: 2.5, sm: 3 },
              boxShadow: "0 4px 20px rgba(15,23,42,0.06)",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="NIYANTRIX"
              sx={{
                width: { xs: 55, sm: 70 },
                height: { xs: 55, sm: 70 },
                objectFit: "contain",
              }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: "1.8rem", sm: "2.4rem" },
                  fontWeight: 800,
                  color: "#1e3a8a",
                  letterSpacing: "1px",
                  lineHeight: 1,
                }}
              >
                NIYANTRIX
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                  fontWeight: 700,
                  color: "#1e3a8a",
                  letterSpacing: "2px",
                  mt: 0.75,
                }}
              >
                ASSET MANAGEMENT SOFTWARE
              </Typography>
            </Box>
          </Box>

          {/* Title */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: "#0f172a",
              textAlign: "center",
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.25rem" },
              mt: 2,
            }}
          >
            NIYANTRIX Asset Management
          </Typography>

          {/* Personalized welcome */}
          <Typography
            sx={{
              color: "#64748b",
              textAlign: "center",
              fontSize: { xs: "0.95rem", sm: "1.05rem" },
            }}
          >
            {userInfo.name
              ? `Welcome back, ${userInfo.name}${userInfo.company ? ` • ${userInfo.company}` : ""}`
              : "Scalable Solutions for Modern Enterprises"}
          </Typography>

          {/* Proceed CTA → My Assets */}
          <Button
            onClick={handleProceed}
            endIcon={<ArrowRight size={18} />}
            sx={{
              mt: 2,
              bgcolor: "#2563eb",
              color: "#fff",
              fontWeight: 700,
              fontSize: "1rem",
              textTransform: "none",
              borderRadius: 999,
              px: 4,
              py: 1.5,
              boxShadow: "0 6px 16px rgba(37,99,235,0.35)",
              "&:hover": {
                bgcolor: "#1d4ed8",
                boxShadow: "0 8px 20px rgba(37,99,235,0.45)",
              },
            }}
          >
            Proceed
          </Button>
        </Stack>
      </Container>

      {/* ══════════ FEATURE GRID ══════════ */}
      <Container maxWidth="lg" sx={{ pb: { xs: 5, md: 8 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          {features.map((feat, i) => (
            <Paper
              key={i}
              sx={{
                p: { xs: 3, sm: 3.5 },
                borderRadius: 2,
                border: "1px solid #eef0f3",
                boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
                bgcolor: "#fff",
                transition: "transform .18s ease, box-shadow .18s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "1.35rem", sm: "1.5rem" },
                  fontWeight: 700,
                  color: "#0f172a",
                  mb: 1.5,
                }}
              >
                {feat.title}
              </Typography>
              <Typography
                sx={{ color: "#64748b", fontSize: "0.95rem", lineHeight: 1.5 }}
              >
                {feat.desc}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* ══════════ FOOTER ══════════ */}
      <Box
        component="footer"
        sx={{
          bgcolor: "#fff",
          borderTop: "1px solid #eef0f3",
          py: { xs: 4, md: 5 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.4rem" },
              fontWeight: 700,
              color: "#0f172a",
              mb: 1.5,
            }}
          >
            Connect With Our Team
          </Typography>
          <Typography sx={{ color: "#475569", mb: 2, fontSize: "1rem" }}>
            Dedicated Support:{" "}
            <Box component="strong" sx={{ color: "#0f172a", fontWeight: 700 }}>
              +91 6360919373
            </Box>
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 0.5, sm: 1.5 }}
            justifyContent="center"
            alignItems="center"
            divider={
              <Box sx={{ display: { xs: "none", sm: "block" }, color: "#cbd5e1" }}>
                |
              </Box>
            }
          >
            {["Schedule Live Demo", "Enterprise API", "Partnership Opportunities"].map((label) => (
              <Typography key={label} component="span"
                sx={{
                  color: "#2563eb", fontSize: "0.95rem", cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {label}
              </Typography>
            ))}
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default UserDashboard;